import { store } from "@stencil/redux";
import {
  WorkoutLog,
  User,
  Integrations,
  UserMonthlyStatsDoc,
  StatWorkoutLog,
  LearningLibraryLog,
} from "@sworkit/api";
import { endOfDay, format, startOfDay } from "date-fns";
import {
  getDocs,
  query,
  collection,
  where,
  orderBy,
  onSnapshot,
  limit,
  startAfter,
  doc,
  setDoc,
  deleteDoc,
  collectionGroup,
  FieldValue,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firebaseFunctions, fst as firestore } from "./firebase";
import { getLoggedInUserId } from "./firestore-user";
import { firestoreCollectionGet } from "./firestore-utils";
import {
  getChallengeIdsFromUser,
  getHealthLogType,
  removeUndefinedAndNaNRecursive,
} from "./utils";
import { Capacitor } from "@capacitor/core";
import {
  createDateAsUTC,
  js_yyyy_mm_dd_hh_mm_ss,
} from "../actions/workout-logs";
import { jsVersionWithBuildNumber } from "./app";
import { getDeviceType } from "./sw-device";
import { healthIntegrationKeyToName } from "./integrations";

export async function getStatsLogsDataBetweenTimestamps(
  fromTimestamp: number,
  toTimestamp: number,
  uid?: string,
) {
  if (!firestore) return;

  const searchUid = uid || getLoggedInUserId();
  const fromYearMonth = format(fromTimestamp, "yyyy-MM");
  const toYearMonth = format(toTimestamp, "yyyy-MM");
  const queryResult = await getDocs(
    query(
      collection(firestore, `/users/${searchUid}/stats`),
      where("monthYear", ">=", fromYearMonth),
      where("monthYear", "<=", toYearMonth),
      orderBy("monthYear", "desc"),
    ),
  );
  const statsDocs = queryResult.docs.map(
    (doc) => doc.data() as UserMonthlyStatsDoc,
  );
  return getStatLogsFromStatsDocs(statsDocs, fromTimestamp, toTimestamp);
}

function getStatLogsFromStatsDocs(
  statsDocs: UserMonthlyStatsDoc[],
  fromTimestamp: number,
  toTimestamp: number,
) {
  const logs: StatWorkoutLog[] = [];
  statsDocs.map((doc) => {
    if (doc && doc.workoutLogs) {
      Object.keys(doc.workoutLogs).map((logId) => {
        const log = doc.workoutLogs[logId];
        if (
          log &&
          log.timestampMillis &&
          log.timestampMillis >= fromTimestamp &&
          log.timestampMillis <= toTimestamp
        ) {
          logs.push(log);
        }
      });
    }
  });
  return logs;
}

export function listenToWorkoutLogsBetweenTimestamps(
  fromTimestamp: number,
  toTimestamp: number,
  callback: (workoutLogs: WorkoutLog[]) => void,
) {
  if (!firestore) return;
  const uid = getLoggedInUserId();
  const q = query(
    collection(firestore, `/users/${uid}/workoutLogs`),
    where("timestampMillis", ">=", fromTimestamp),
    where("timestampMillis", "<=", toTimestamp),
    orderBy("timestampMillis", "desc"),
  );
  return onSnapshot(q, (snap) => {
    const logs = snap.docs.map((doc) => doc.data() as WorkoutLog);
    callback(logs);
  });
}

export async function getWorkoutLogsOnDate(date: Date) {
  if (!firestore) return;

  const dayBeginsMillis = startOfDay(date).valueOf();
  const dayEndsMillis = endOfDay(date).valueOf();
  const uid = getLoggedInUserId();
  const q = query(
    collection(firestore, `/users/${uid}/workoutLogs`),
    where("timestampMillis", ">=", dayBeginsMillis),
    where("timestampMillis", "<=", dayEndsMillis),
    orderBy("timestampMillis", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((doc) => doc.data() as WorkoutLog);
}

export async function getLatestHundredWorkoutLogs(startAfterNumber?: number) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return [];
  try {
    let q = query(
      collection(firestore, `users/${uid}/workoutLogs`),
      orderBy("timestampMillis", "desc"),
      limit(100),
    );
    if (startAfterNumber) {
      q = query(q, startAfter(startAfterNumber));
    }
    const workoutLogs = await firestoreCollectionGet<WorkoutLog>(q);
    return workoutLogs;
  } catch (err) {
    console.error(err);
    return [];
  }
}

export function addAssignmentIdIfLogMeetsAssignment(
  userAssignmentState: UserAssignmentsState,
  newLog: WorkoutLog,
): WorkoutLog {
  newLog.assignmentIds = [];
  const publishedAssignments = getPublishedAssignments(
    userAssignmentState.assignments,
  );
  if (publishedAssignments.length) {
    const validAssignments = checkAssignmentDate(publishedAssignments);
    validAssignments.forEach((assignment) => {
      if (
        assignment.workouts.length > 0 &&
        assignment.workouts.includes(newLog.slug)
      ) {
        newLog.assignmentIds.push(assignment.assignmentId);
      }
      if (assignment.workouts.length === 0) {
        newLog.assignmentIds.push(assignment.assignmentId);
      }
    });
  }
  return newLog;
}

export function getPublishedAssignments(
  assignments: Assignment[],
): Assignment[] {
  return assignments.filter((assignment) => assignment.status === "published");
}

export function checkAssignmentDate(assignments: Assignment[]): Assignment[] {
  return assignments.filter((assignment) => {
    if (!assignment.dueDate) return false;
    const d = new Date(assignment.dueDate);
    return d > new Date() ? true : false;
  });
}

export async function assessmentLogFromPartial(
  log: Partial<WorkoutLog> & {
    name: string;
    calories: number;
    slug: string;
    minutesCompleted: number;
  },
  user: UserState,
) {
  const date = new Date();
  const createdOn = js_yyyy_mm_dd_hh_mm_ss(date);
  const utcCreated = js_yyyy_mm_dd_hh_mm_ss(createDateAsUTC(date));
  const appVersion = jsVersionWithBuildNumber;
  const deviceType = await getDeviceType();
  // const state = getState();
  const { name, calories, slug, minutesCompleted } = log;
  const newActivityLog: WorkoutLog = {
    activityKey: "interval_training",
    appVersion,
    platform: Capacitor.getPlatform() as "ios" | "android" | "web",
    calories,
    connectedApps: "[]",
    createdOn,
    heartRate: "",
    slug,
    minutesCompleted,
    deviceType,
    name,
    collectionSlug: "assessments",
    timestampMillis: date.valueOf(),
    type: "cardio",
    utcCreated,
    shouldSaveToHealth: true,
    savedToHealth: false,
    savedToHealthIntegrations: [],
    category: null,
    logType: "assessment",
    challengeIds: getChallengeIdsFromUser(user),
    externalUuid: null,
    assignmentIds: [],
  };
  return newActivityLog;
}

export async function studioLogFromPartial(
  log: Partial<WorkoutLog> & {
    name: string;
    calories: number;
    slug: string;
    minutesCompleted: number;
  },
  user: UserState,
) {
  const date = new Date();
  const createdOn = js_yyyy_mm_dd_hh_mm_ss(date);
  const utcCreated = js_yyyy_mm_dd_hh_mm_ss(createDateAsUTC(date));
  const appVersion = jsVersionWithBuildNumber;
  const deviceType = await getDeviceType();
  // const state = getState();
  const { name, calories, slug, minutesCompleted, collectionSlug, type } = log;
  const newActivityLog: WorkoutLog = {
    activityKey: "interval_training",
    appVersion,
    platform: Capacitor.getPlatform() as "ios" | "android" | "web",
    calories,
    connectedApps: "[]",
    createdOn,
    heartRate: "",
    slug,
    minutesCompleted,
    deviceType,
    name,
    collectionSlug,
    timestampMillis: date.valueOf(),
    type,
    utcCreated,
    shouldSaveToHealth: true,
    savedToHealth: false,
    savedToHealthIntegrations: [],
    category: null,
    logType: "studio",
    challengeIds: getChallengeIdsFromUser(user),
    externalUuid: null,
    assignmentIds: [],
  };
  return newActivityLog;
}

export async function addWorkoutLog(
  workoutLog: WorkoutLog,
  userDoc: User,
  callback?: () => void,
) {
  const uid = getLoggedInUserId();
  const { userAssignments } = store.getState();
  addAssignmentIdIfLogMeetsAssignment(userAssignments, workoutLog);
  workoutLog.logType = workoutLog.logType ? workoutLog.logType : "sworkout";
  if (userDoc && userDoc.account) {
    workoutLog.groups = userDoc.account.groups || [];
    workoutLog.orgId = userDoc.account.orgId || null;
  }
  workoutLog.challengeIds = getChallengeIdsFromUser(userDoc);

  if (uid && firestore) {
    const baseRef = collection(firestore, `users/${uid}/workoutLogs`);
    const externalWorkoutId =
    //hacky
      workoutLog.externalUuid && healthIntegrationKeyToName[workoutLog.logType]
        ? workoutLog.externalUuid
        : null;
    const existingId = externalWorkoutId || workoutLog.id;

    const newDoc = existingId ? doc(baseRef, existingId) : doc(baseRef);
    workoutLog.id = newDoc.id;
    workoutLog.calories = workoutLog.calories
      ? Math.round(workoutLog.calories)
      : 0;
    try {
      if (workoutLog.minutesCompleted >= 1) {
        await setDoc(newDoc, workoutLog);
      } else {
        console.debug(
          "workout log shorter than 1 minute, did not save to firestore",
        );
      }
      callback?.();
    } catch (err) {
      console.error("workout log not saved", err);
    }
  }
  return workoutLog;
}

export async function addLearningLibraryLog(
  learningLibraryLog: LearningLibraryLog,
  userDoc: User,
  callback?: () => void,
) {
  const uid = getLoggedInUserId();
  // const { userAssignments } = store.getState();
  // addAssignmentIdIfLogMeetsAssignment(userAssignments, workoutLog);
  if (!learningLibraryLog) {
    throw new Error("no nutrition log found");
  }
  const { logType } = learningLibraryLog;
  learningLibraryLog.logType = logType ? logType : "content";
  if (userDoc && userDoc.account) {
    learningLibraryLog.groups = userDoc.account.groups || [];
    learningLibraryLog.orgId = userDoc.account.orgId || null;
  }
  learningLibraryLog.challengeIds = getChallengeIdsFromUser(userDoc);

  if (uid && firestore) {
    const baseRef = collection(firestore, `users/${uid}/learningLibraryLogs`);
    const existingId = learningLibraryLog.id;

    const newDoc = existingId ? doc(baseRef, existingId) : doc(baseRef);
    learningLibraryLog.id = newDoc.id;
    try {
      // if (learningLibraryLog.minutesCompleted >= 1) {
      await setDoc(newDoc, removeUndefinedAndNaNRecursive(learningLibraryLog));
      // } else {
      //   console.debug("nutrition log shorter than 1 minute, did not save to firestore")
      // }
      callback?.();
    } catch (err) {
      console.error("nutrition log not saved", err);
    }
  }
  return learningLibraryLog;
}
type WorkoutLogUpdate = {
  [K in keyof WorkoutLog]?: WorkoutLog[K] | FieldValue;
};
export async function updateWorkoutLog(
  logId: string,
  workoutLog: WorkoutLogUpdate,
) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  return setDoc(
    doc(firestore, `users/${uid}/workoutLogs/${logId}`),
    workoutLog,
    { merge: true },
  );
}

export async function saveActivityLog(activityLog: WorkoutLog, userDoc: User) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  activityLog.logType = "external-activity";
  if (userDoc && userDoc.account) {
    activityLog.groups = userDoc.account.groups || [];
    activityLog.orgId = userDoc.account.orgId || null;
  }
  const docRef = doc(collection(firestore, `users/${uid}/workoutLogs`));
  activityLog.id = docRef.id;
  return setDoc(docRef, activityLog);
}

export async function deleteWorkoutLog(id: string) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  return deleteDoc(doc(firestore, `users/${uid}/workoutLogs/${id}`));
}

//todo: use this?
export async function sendLogToIntegration(
  integrations: Integrations,
  workoutLog: WorkoutLog,
  integrationSlug: "mfp" | "strava" | "fitbit",
) {
  if (!firestore) return;
  const sendLogToIntegration = httpsCallable(
    firebaseFunctions,
    "sendLogToIntegration",
  );
  const result = await sendLogToIntegration({
    integrations,
    workoutLog,
    integrationSlug,
  });
  return result.data;
}

export function recentWorkoutLogsListener(
  queryLimit: number,
  callback: (workouts: WorkoutLog[]) => void,
) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  const q = query(
    collection(firestore, `users/${uid}/workoutLogs`),
    orderBy("timestampMillis", "desc"),
    limit(queryLimit),
  );

  return onSnapshot(q, (snap) => {
    const workoutLogs = snap.docs.map((doc) => doc.data() as WorkoutLog);
    callback(workoutLogs);
  });
}

export async function getSharedWorkoutLog(workoutLogId: string) {
  const uid = getLoggedInUserId();
  if (!firestore || !uid) return;
  const queryResult = await getDocs(
    query(
      collectionGroup(firestore, "workoutLogs"),
      where("id", "==", workoutLogId),
    ),
  );
  const logs: WorkoutLog[] = queryResult.docs
    .filter((doc) => doc.ref.path.includes("users/"))
    .filter((doc) => doc.id === workoutLogId)
    .map((doc) => doc.data() as WorkoutLog);
  return logs[0];
}

export function healthLogListener(
  queryLimit: number,
  callback: (workouts: WorkoutLog[]) => void,
) {
  const uid = getLoggedInUserId();
  const platform = getHealthLogType();
  if (!uid || !firestore || !platform) return;
  const q = query(
    collection(firestore, `users/${uid}/workoutLogs`),
    where("logType", "==", platform),
    orderBy("timestampMillis", "desc"),
    limit(queryLimit),
  );
  return onSnapshot(q, (snap) => {
    const workoutLogs = snap.docs.map((doc) => doc.data() as WorkoutLog);
    callback(workoutLogs);
  });
}
